import React, { useState, useEffect } from 'react';
import { Cpu, Key, Plus, Eye, EyeOff, CheckCircle, Copy, Edit2, Check, X, Trash2, Search, Clock, Sparkles, Database, RefreshCw } from 'lucide-react';
import { AppConfig } from '../types';
import { ConfirmModal } from './ConfirmModal';

interface ApiIntegrationSettingsProps {
  localConfig: AppConfig;
  setLocalConfig: (config: AppConfig) => void;
  currentUser: any;
  sessions: any[];
  toast?: {
    success: (message: string) => void;
    error: (message: string) => void;
    warning: (message: string) => void;
    info: (message: string) => void;
  };
}

export const ApiIntegrationSettings: React.FC<ApiIntegrationSettingsProps> = ({
  localConfig,
  setLocalConfig,
  currentUser,
  sessions,
  toast,
}) => {
  // API Keys States
  const [apiKeys, setApiKeys] = useState<any[]>([]);
  const [newApiKey, setNewApiKey] = useState<{ name: string; expiresOption: string; customDays: number }>({
    name: '',
    expiresOption: 'never',
    customDays: 30
  });
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [apiKeySearchQuery, setApiKeySearchQuery] = useState('');
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [orgId, setOrgId] = useState<number | null>(null);
  const [showDeleteKeyConfirm, setShowDeleteKeyConfirm] = useState(false);
  const [keyToDeleteId, setKeyToDeleteId] = useState<number | null>(null);
  const [editingKeyId, setEditingKeyId] = useState<number | null>(null);
  const [editingKeyName, setEditingKeyName] = useState('');
  const [editingKeyExpiresOption, setEditingKeyExpiresOption] = useState('keep');
  const [editingKeyCustomDays, setEditingKeyCustomDays] = useState(30);
  const [revealedKeys, setRevealedKeys] = useState<Record<number, boolean>>({});
  
  // BotADesk & Event Logs States
  const [configuringBotADesk, setConfiguringBotADesk] = useState(false);
  const [eventLogs, setEventLogs] = useState<any[]>([]);
  const [loadingEventLogs, setLoadingEventLogs] = useState(false);
  const [webhookSearch, setWebhookSearch] = useState('');

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast?.success(`¡${label} copiado al portapapeles!`);
  };

  const getExpirationBadge = (expiresAt?: string | null) => {
    if (!expiresAt) {
      return (
        <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded font-medium bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
          <CheckCircle size={10} /> Nunca expira
        </span>
      );
    }
    const expDate = new Date(expiresAt);
    const now = new Date();
    const isExpired = expDate.getTime() < now.getTime();
    const formatted = expDate.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
    
    if (isExpired) {
      return (
        <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded font-medium bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800">
          <X size={10} /> Expirada ({formatted})
        </span>
      );
    }

    const diffDays = Math.max(0, Math.ceil((expDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
    return (
      <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded font-medium bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
        <Clock size={10} /> Expira en {diffDays}d ({formatted})
      </span>
    );
  };

  const fetchApiKeys = async (targetOrgId?: number) => {
    const currentOrgId = targetOrgId || orgId;
    if (!currentOrgId || !currentUser?.id) return;
    setLoadingKeys(true);
    try {
      const headers = { 
        'x-user-id': currentUser.id.toString(), 
        'Content-Type': 'application/json' 
      };
      const keysRes = await fetch(`/api/org/api-keys?org_id=${currentOrgId}`, { headers }).then(r => r.json());
      if (keysRes.success) {
        setApiKeys(keysRes.keys || []);
      }
    } catch (e) {
      console.error("Error al cargar API Keys", e);
    } finally {
      setLoadingKeys(false);
    }
  };

  const fetchEventLogs = async (targetOrgId?: number) => {
    const currentOrgId = targetOrgId || orgId;
    if (!currentOrgId || !currentUser?.id) return;
    setLoadingEventLogs(true);
    try {
      const headers = { 
        'x-user-id': currentUser.id.toString(), 
        'Content-Type': 'application/json' 
      };
      const res = await fetch(`/api/org/event-logs?org_id=${currentOrgId}`, { headers }).then(r => r.json());
      if (res.success) {
        setEventLogs(res.logs || []);
      }
    } catch (e) {
      console.error("Error al cargar Event Logs", e);
    } finally {
      setLoadingEventLogs(false);
    }
  };

  // Cargar organizaciones al montar
  useEffect(() => {
    const fetchOrgs = async () => {
      if (!currentUser?.id) return;
      try {
        const headers = { 
          'x-user-id': currentUser.id.toString(), 
          'Content-Type': 'application/json' 
        };
        const res = await fetch('/api/organizations', { headers }).then(r => r.json());
        if (res.success && res.organizations && res.organizations.length > 0) {
          setOrganizations(res.organizations);
          
          const userDefaultId = currentUser?.default_organization_id;
          const exists = res.organizations.find((o: any) => o.id === userDefaultId);
          const initialOrgId = exists ? exists.id : res.organizations[0].id;
          
          setOrgId(initialOrgId);
          fetchApiKeys(initialOrgId);
          fetchEventLogs(initialOrgId);
        }
      } catch (err) {
        console.error("Error fetching organizations in ApiIntegrationSettings", err);
      }
    };
    
    fetchOrgs();
  }, [currentUser?.id]);

  useEffect(() => {
    if (orgId) {
      fetchApiKeys(orgId);
      fetchEventLogs(orgId);
    }
  }, [orgId]);

  const handleGenerateApiKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newApiKey.name.trim() || !orgId) return;
    try {
      const headers = { 
        'x-user-id': currentUser.id.toString(), 
        'Content-Type': 'application/json' 
      };

      let expiresInDays: number | null = null;
      if (newApiKey.expiresOption === 'custom') {
        expiresInDays = newApiKey.customDays > 0 ? Number(newApiKey.customDays) : null;
      } else if (newApiKey.expiresOption !== 'never') {
        expiresInDays = Number(newApiKey.expiresOption);
      }

      const res = await fetch(`/api/org/api-keys?org_id=${orgId}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name: newApiKey.name.trim(),
          expires_in_days: expiresInDays,
          rate_limit_per_minute: 60
        })
      }).then(r => r.json());

      if (res.success) {
        toast?.success("API Key generada y cifrada con éxito");
        setGeneratedKey(res.apiKey);
        setNewApiKey({ name: '', expiresOption: 'never', customDays: 30 });
        fetchApiKeys(orgId);
      } else {
        toast?.error(res.message || "Error al generar API Key");
      }
    } catch (e) {
      toast?.error("Error al generar API Key");
    }
  };

  const handleDeleteApiKey = (id: number) => {
    setKeyToDeleteId(id);
    setShowDeleteKeyConfirm(true);
  };

  const confirmDeleteApiKey = async () => {
    if (keyToDeleteId === null) return;
    try {
      const headers = { 
        'x-user-id': currentUser.id.toString(), 
        'Content-Type': 'application/json' 
      };
      const res = await fetch(`/api/org/api-keys/${keyToDeleteId}?org_id=${orgId}`, {
        method: 'DELETE',
        headers
      }).then(r => r.json());
      if (res.success) {
        toast?.success("API Key eliminada correctamente");
        fetchApiKeys(orgId);
      } else {
        toast?.error(res.message || "Error al eliminar API Key");
      }
    } catch (e) {
      toast?.error("Error al eliminar API Key");
    } finally {
      setKeyToDeleteId(null);
    }
  };

  const startEditingKey = (k: any) => {
    setEditingKeyId(k.id);
    setEditingKeyName(k.name);
    setEditingKeyExpiresOption('keep');
    setEditingKeyCustomDays(30);
  };

  const handleUpdateApiKey = async (id: number) => {
    if (!editingKeyName.trim()) return;
    try {
      const headers = { 
        'x-user-id': currentUser.id.toString(), 
        'Content-Type': 'application/json' 
      };

      const payload: any = { name: editingKeyName.trim() };
      if (editingKeyExpiresOption === 'never') {
        payload.clear_expiry = true;
      } else if (editingKeyExpiresOption === 'custom') {
        if (editingKeyCustomDays > 0) {
          payload.expires_in_days = Number(editingKeyCustomDays);
        } else {
          payload.clear_expiry = true;
        }
      } else if (editingKeyExpiresOption !== 'keep') {
        payload.expires_in_days = Number(editingKeyExpiresOption);
      }

      const res = await fetch(`/api/org/api-keys/${id}?org_id=${orgId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload)
      }).then(r => r.json());
      if (res.success) {
        toast?.success("API Key actualizada correctamente");
        setEditingKeyId(null);
        fetchApiKeys(orgId);
      } else {
        toast?.error(res.message || "Error al actualizar API Key");
      }
    } catch (e) {
      toast?.error("Error al actualizar API Key");
    }
  };

  const handleAutoConfigureBotADesk = async () => {
    if (!orgId) {
      toast?.error("No se encontró una organización activa para realizar la configuración automática.");
      return;
    }
    setConfiguringBotADesk(true);
    try {
      const headers = { 
        'x-user-id': currentUser.id.toString(), 
        'Content-Type': 'application/json' 
      };
      
      const templatesRes = await fetch(`/api/org/notification-templates?org_id=${orgId}`, { headers }).then(r => r.json());
      let templateGoId = null;
      let templateMetaId = null;

      if (templatesRes.success && templatesRes.templates) {
        const existingGo = templatesRes.templates.find((t: any) => t.code === 'botadesk_alert_go');
        if (existingGo) {
          templateGoId = existingGo.id;
        }
        const existingMeta = templatesRes.templates.find((t: any) => t.code === 'botadesk_alert_meta');
        if (existingMeta) {
          templateMetaId = existingMeta.id;
        }
      }

      if (!templateGoId) {
        const newTemplateGo = {
          name: 'Alerta BotADesk (WhatyBot Go)',
          code: 'botadesk_alert_go',
          provider: 'whatsmeow',
          body: '{{message_content}}',
          variables: [{ name: 'message_content', path: 'message_content' }],
          active: true
        };
        const createRes = await fetch(`/api/org/notification-templates?org_id=${orgId}`, {
          method: 'POST',
          headers,
          body: JSON.stringify(newTemplateGo)
        }).then(r => r.json());
        if (createRes.success) {
          templateGoId = createRes.template.id;
        }
      }

      if (!templateMetaId) {
        const newTemplateMeta = {
          name: 'Alerta BotADesk (Meta)',
          code: 'botadesk_alert_meta',
          provider: 'meta_cloud_api',
          meta_template_name: 'botadesk_alert',
          body: '{{message_content}}',
          variables: [{ name: 'message_content', path: 'message_content' }],
          active: true
        };
        const createRes = await fetch(`/api/org/notification-templates?org_id=${orgId}`, {
          method: 'POST',
          headers,
          body: JSON.stringify(newTemplateMeta)
        }).then(r => r.json());
        if (createRes.success) {
          templateMetaId = createRes.template.id;
        }
      }

      const rulesRes = await fetch(`/api/org/notification-rules?org_id=${orgId}`, { headers }).then(r => r.json());
      let ruleExists = false;
      if (rulesRes.success && rulesRes.rules) {
        ruleExists = rulesRes.rules.some((r: any) => r.event_type === 'notificacion.directa');
      }

      if (!ruleExists && templateGoId) {
        const newRule = {
          name: 'Envío Directo BotADesk',
          event_type: 'notificacion.directa',
          recipient_mode: 'payload_phone',
          template_id: templateGoId,
          send_mode: 'immediate',
          enabled: true,
          priority: 10
        };
        await fetch(`/api/org/notification-rules?org_id=${orgId}`, {
          method: 'POST',
          headers,
          body: JSON.stringify(newRule)
        });
      }

      toast?.success("¡Configuración de BotADesk autocompletada con éxito!");
    } catch (e) {
      toast?.error("Error al configurar automáticamente BotADesk.");
    } finally {
      setConfiguringBotADesk(false);
    }
  };

  return (
    <div className="bg-theme-card p-6 rounded-xl shadow-sm border border-theme md:col-span-2">
      <h3 className="text-lg font-bold text-theme-main mb-4 flex items-center gap-2">
        <Cpu size={20} className="text-blue-600" /> Configuración de Integración de API (WhatyBot Go / Meta)
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Proveedor */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-theme-main">
            Proveedor de Integración
          </label>
          <select
            className="w-full px-3 py-2 border border-theme rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm bg-theme-card"
            value={localConfig.integrationProvider || ''}
            onChange={(e) => setLocalConfig({ 
              ...localConfig, 
              integrationProvider: e.target.value,
            })}
          >
            <option value="">Por defecto (Configuración global del servidor)</option>
            <option value="whatsmeow">WhatyBot Go (Multi-sesión / WhatsApp Web)</option>
            <option value="meta">Meta Cloud API (Oficial)</option>
          </select>
          <p className="text-xs text-theme-muted">
            Elige el canal a través del cual tu bot procesará y enviará los mensajes recibidos mediante la API.
          </p>
        </div>

        {/* Sesión de Fallback */}
        {localConfig.integrationProvider === 'whatsmeow' && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-theme-main">
              Sesión de Fallback (Respaldo)
            </label>
            <select
              className="w-full px-3 py-2 border border-theme rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm bg-theme-card"
              value={localConfig.integrationFallbackSession || ''}
              onChange={(e) => setLocalConfig({ 
                ...localConfig, 
                integrationFallbackSession: e.target.value 
              })}
            >
              <option value="">Ninguna (Sin fallback)</option>
              {sessions.map((s) => (
                <option key={s.sessionId} value={s.sessionId}>
                  {s.phoneNumber ? `+${s.phoneNumber.replace('+', '')}` : s.sessionId.substring(0, 8)}
                </option>
              ))}
            </select>
            <p className="text-xs text-theme-muted">
              Si las sesiones principales de envío fallan o no están disponibles, el sistema intentará enviar el mensaje usando este número de fallback.
            </p>
          </div>
        )}
      </div>

      {/* Selección de Múltiples Sesiones para WhatyBot Go */}
      {localConfig.integrationProvider === 'whatsmeow' && (
        <div className="mt-6 border-t border-theme pt-4 space-y-4">
          <label className="block text-sm font-medium text-theme-main">
            Sesiones de WhatsApp Activas para Envíos (Elección Múltiple)
          </label>
          
          {sessions.length === 0 ? (
            <div className="p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 rounded-lg">
              <p className="text-xs text-amber-700 dark:text-amber-400">
                ⚠️ No tienes sesiones/números de WhatsApp activos en este momento. Por favor ve a la sección de <strong>Conexiones</strong> en el menú lateral para iniciar sesión con al menos un número y poder seleccionarlo aquí.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {sessions.map((s) => {
                const selectedSessions = localConfig.integrationSessions || [];
                const isChecked = selectedSessions.includes(s.sessionId);
                return (
                  <label
                    key={s.sessionId}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      isChecked
                        ? 'border-primary-500 bg-primary-50/50 dark:bg-primary-950/20'
                        : 'border-theme hover:bg-theme-base'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      checked={isChecked}
                      onChange={(e) => {
                        let nextSessions = [...selectedSessions];
                        if (e.target.checked) {
                          nextSessions.push(s.sessionId);
                        } else {
                          nextSessions = nextSessions.filter((id) => id !== s.sessionId);
                        }
                        setLocalConfig({
                          ...localConfig,
                          integrationSessions: nextSessions,
                        });
                      }}
                    />
                    <div className="flex flex-col min-w-0">
                      <span className="text-xs font-semibold text-theme-main truncate">
                        {s.phoneNumber ? `+${s.phoneNumber.replace('+', '')}` : 'Sin número'}
                      </span>
                      <span className={`text-[10px] ${s.isReady ? 'text-primary-600' : 'text-slate-400'}`}>
                        {s.isReady ? 'Conectado' : s.status}
                      </span>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
          <p className="text-xs text-theme-muted mt-2">
            Puedes seleccionar múltiples números. El bot distribuirá/rotará los envíos de la API entre los números marcados que estén activos.
          </p>
        </div>
      )}

      {/* Gestión de API Keys */}
      {orgId && (
        <div className="mt-6 border-t border-theme pt-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
            <h4 className="text-sm font-bold text-theme-main flex items-center gap-2">
              <Key size={18} className="text-primary-500" />
              Mis API Keys de Integración
            </h4>
            {organizations.length > 1 && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-theme-muted font-medium">Organización:</span>
                <select
                  value={orgId}
                  onChange={(e) => setOrgId(Number(e.target.value))}
                  className="bg-theme-base text-theme-main rounded-lg px-3 py-1.5 text-xs border border-theme focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name} ({org.slug.toUpperCase()})
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Formulario y listado */}
            <div className="lg:col-span-1 space-y-4">
              <form onSubmit={handleGenerateApiKey} className="bg-theme-base p-4 rounded-xl border border-theme space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold text-theme-muted uppercase tracking-wider">Generar Nueva Clave</p>
                  <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold bg-emerald-50 dark:bg-emerald-950/40 px-1.5 py-0.5 rounded border border-emerald-200 dark:border-emerald-900/50">
                    AES-256
                  </span>
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-theme-muted mb-1">Nombre de la Integración</label>
                  <input 
                    type="text" 
                    placeholder="Ej. CRM, BotADesk, Zapier" 
                    value={newApiKey.name}
                    onChange={e => setNewApiKey({...newApiKey, name: e.target.value})}
                    className="w-full bg-theme-card text-theme-main placeholder-theme-muted rounded-lg px-3 py-2 text-xs border border-theme focus:outline-none focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-theme-muted mb-1">Expiración</label>
                  <select
                    value={newApiKey.expiresOption}
                    onChange={e => setNewApiKey({...newApiKey, expiresOption: e.target.value})}
                    className="w-full bg-theme-card text-theme-main rounded-lg px-3 py-2 text-xs border border-theme focus:outline-none focus:border-primary-500"
                  >
                    <option value="never">🟢 Nunca expira (Recomendado)</option>
                    <option value="30">30 días</option>
                    <option value="60">60 días</option>
                    <option value="90">90 días</option>
                    <option value="365">1 año (365 días)</option>
                    <option value="custom">Personalizado (días)</option>
                  </select>
                </div>
                {newApiKey.expiresOption === 'custom' && (
                  <div>
                    <label className="block text-[11px] font-medium text-theme-muted mb-1">Días de vigencia:</label>
                    <input 
                      type="number" 
                      min="1"
                      max="3650"
                      value={newApiKey.customDays}
                      onChange={e => setNewApiKey({...newApiKey, customDays: Math.max(1, parseInt(e.target.value) || 1)})}
                      className="w-full bg-theme-card text-theme-main rounded-lg px-3 py-1.5 text-xs border border-theme focus:outline-none focus:border-primary-500"
                    />
                  </div>
                )}
                <button type="submit" className="w-full bg-primary-600 hover:bg-primary-500 text-white py-2 rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 shadow-sm">
                  <Plus size={14} /> Generar API Key
                </button>
              </form>

              {generatedKey && (
                <div className="bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/40 p-4 rounded-xl space-y-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-400">
                      <CheckCircle size={16} />
                      <span className="text-xs font-bold font-sans">Nueva clave generada:</span>
                    </div>
                    <div className="flex gap-2">
                      <p className="text-xs font-mono bg-slate-900 dark:bg-slate-950 p-2 rounded text-slate-100 border border-slate-700/50 break-all select-all flex-1">{generatedKey}</p>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(generatedKey, 'API Key')}
                        className="bg-emerald-600 hover:bg-emerald-500 text-white p-2 rounded-lg transition-colors flex items-center justify-center shrink-0"
                        title="Copiar API Key"
                      >
                        <Copy size={14} />
                      </button>
                    </div>
                    <p className="text-[10px] text-slate-500 dark:text-slate-400">Guardada con cifrado reversible seguro. También podrás copiarla o revelarla desde la lista.</p>
                  </div>

                  <div className="space-y-1 border-t border-emerald-200 dark:border-emerald-900/30 pt-2">
                    <span className="text-xs font-bold text-emerald-800 dark:text-emerald-400 font-sans">URL del Endpoint para tu App:</span>
                    <div className="flex gap-2">
                      <p className="text-xs font-mono bg-slate-900 dark:bg-slate-950 p-2 rounded text-slate-100 border border-slate-700/50 break-all select-all flex-1">
                        {`${window.location.origin}/api/integrations/events`}
                      </p>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(`${window.location.origin}/api/integrations/events`, 'URL del Endpoint')}
                        className="bg-emerald-600 hover:bg-emerald-500 text-white p-2 rounded-lg transition-colors flex items-center justify-center shrink-0"
                        title="Copiar URL"
                      >
                        <Copy size={14} />
                      </button>
                    </div>
                    <p className="text-[10px] text-slate-500 dark:text-slate-400">Método: POST | Header: Authorization: Bearer</p>
                  </div>
                </div>
              )}
            </div>

            {/* Listado de Claves */}
            <div className="lg:col-span-2 space-y-4">
              <div className="flex items-center gap-2 bg-theme-base px-2.5 py-1.5 rounded-lg border border-theme text-xs shadow-sm mb-3">
                <Search size={14} className="text-theme-muted" />
                <input 
                  type="text" 
                  placeholder="Buscar API Keys..." 
                  value={apiKeySearchQuery}
                  onChange={e => setApiKeySearchQuery(e.target.value)}
                  className="bg-transparent text-xs text-theme-main focus:outline-none w-full"
                />
              </div>

              <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                {loadingKeys ? (
                  <p className="text-xs text-theme-muted text-center py-4">Cargando API Keys...</p>
                ) : apiKeys.filter(k => 
                  k.name.toLowerCase().includes(apiKeySearchQuery.toLowerCase()) ||
                  k.api_key_prefix.toLowerCase().includes(apiKeySearchQuery.toLowerCase())
                ).length === 0 ? (
                  <p className="text-xs text-theme-muted text-center py-4">No tienes ninguna API Key activa.</p>
                ) : (
                  apiKeys.filter(k => 
                    k.name.toLowerCase().includes(apiKeySearchQuery.toLowerCase()) ||
                    k.api_key_prefix.toLowerCase().includes(apiKeySearchQuery.toLowerCase())
                  ).map(k => (
                    <div key={k.id} className="bg-theme-base p-3.5 rounded-xl border border-theme hover:border-slate-350 dark:hover:border-slate-700 transition-colors space-y-2.5">
                      {editingKeyId === k.id ? (
                        <div className="space-y-3 bg-theme-card p-3 rounded-lg border border-theme">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-theme-main">Editar Clave</span>
                            <div className="flex items-center gap-1.5">
                              <button 
                                type="button" 
                                onClick={() => handleUpdateApiKey(k.id)} 
                                className="text-emerald-600 hover:text-emerald-700 p-1.5 hover:bg-emerald-50 dark:hover:bg-emerald-950/20 rounded-lg transition-colors flex items-center gap-1 text-xs font-semibold"
                                title="Guardar cambios"
                              >
                                <Check size={14} /> Guardar
                              </button>
                              <button 
                                type="button" 
                                onClick={() => setEditingKeyId(null)} 
                                className="text-red-500 hover:text-red-700 p-1.5 hover:bg-red-50 dark:hover:bg-red-950/20 rounded-lg transition-colors flex items-center gap-1 text-xs font-semibold"
                                title="Cancelar"
                              >
                                <X size={14} /> Cancelar
                              </button>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div>
                              <label className="block text-[10px] font-medium text-theme-muted mb-1">Nombre</label>
                              <input 
                                type="text" 
                                value={editingKeyName} 
                                onChange={e => setEditingKeyName(e.target.value)} 
                                className="w-full bg-theme-base text-xs text-theme-main rounded-lg px-2.5 py-1.5 border border-theme focus:outline-none focus:border-primary-500"
                                autoFocus
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-medium text-theme-muted mb-1">Configuración de Expiración</label>
                              <select
                                value={editingKeyExpiresOption}
                                onChange={e => setEditingKeyExpiresOption(e.target.value)}
                                className="w-full bg-theme-base text-theme-main rounded-lg px-2.5 py-1.5 text-xs border border-theme focus:outline-none focus:border-primary-500"
                              >
                                <option value="keep">🔒 Mantener expiración actual</option>
                                <option value="never">🟢 Cambiar a: Nunca expira</option>
                                <option value="30">Cambiar a: 30 días a partir de hoy</option>
                                <option value="60">Cambiar a: 60 días a partir de hoy</option>
                                <option value="90">Cambiar a: 90 días a partir de hoy</option>
                                <option value="365">Cambiar a: 1 año a partir de hoy</option>
                                <option value="custom">Personalizado (días a partir de hoy)</option>
                              </select>
                            </div>
                            {editingKeyExpiresOption === 'custom' && (
                              <div>
                                <label className="block text-[10px] font-medium text-theme-muted mb-1">Días:</label>
                                <input 
                                  type="number" 
                                  min="1"
                                  max="3650"
                                  value={editingKeyCustomDays}
                                  onChange={e => setEditingKeyCustomDays(Math.max(1, parseInt(e.target.value) || 1))}
                                  className="w-full bg-theme-base text-theme-main rounded-lg px-2.5 py-1 text-xs border border-theme focus:outline-none focus:border-primary-500"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <p className="text-xs font-bold text-theme-main truncate">{k.name}</p>
                              <button
                                type="button"
                                onClick={() => startEditingKey(k)}
                                className="text-slate-400 hover:text-primary-600 p-0.5 rounded transition-colors"
                                title="Editar nombre y expiración"
                              >
                                <Edit2 size={12} />
                              </button>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              {getExpirationBadge(k.expires_at)}
                              <span className={`inline-block text-[9px] px-1.5 py-0.5 rounded font-semibold ${k.status === 'active' ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 border border-emerald-200 dark:border-emerald-800' : 'bg-red-50 dark:bg-red-950/30 text-red-650 border border-red-200 dark:border-red-800'}`}>
                                {k.status.toUpperCase()}
                              </span>
                              {k.status === 'active' && (
                                <button 
                                  type="button"
                                  onClick={() => handleDeleteApiKey(k.id)}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30 p-1 rounded-lg transition-colors ml-1"
                                  title="Eliminar API Key"
                                >
                                  <Trash2 size={14} />
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Clave e interacción de copiado/revelado */}
                          <div className="flex items-center gap-1.5 bg-theme-card px-2.5 py-1.5 rounded-lg border border-theme">
                            <span className="text-[11px] font-mono text-theme-main flex-1 truncate select-all">
                              {k.api_key ? (
                                revealedKeys[k.id] ? k.api_key : `${k.api_key_prefix}${'•'.repeat(24)}`
                              ) : (
                                `${k.api_key_prefix}${'•'.repeat(16)} (Clave anterior sin texto plano)`
                              )}
                            </span>
                            
                            {k.api_key && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => setRevealedKeys(prev => ({ ...prev, [k.id]: !prev[k.id] }))}
                                  className="text-theme-muted hover:text-theme-main p-1 hover:bg-theme-base rounded transition-colors"
                                  title={revealedKeys[k.id] ? "Ocultar clave" : "Mostrar clave completa"}
                                >
                                  {revealedKeys[k.id] ? <EyeOff size={13} /> : <Eye size={13} />}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => copyToClipboard(k.api_key, 'API Key')}
                                  className="text-primary-600 hover:text-primary-500 p-1 hover:bg-primary-50 dark:hover:bg-primary-950/30 rounded transition-colors"
                                  title="Copiar API Key"
                                >
                                  <Copy size={13} />
                                </button>
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sección Especial: Integración de Alertas de BotADesk */}
      <div className="mt-6 border-t border-theme pt-6">
        <div className="bg-gradient-to-r from-blue-500/10 to-indigo-500/10 dark:from-blue-950/20 dark:to-indigo-950/20 border border-blue-200 dark:border-indigo-900/40 p-5 rounded-xl space-y-4">
          <div className="flex items-center gap-3">
            <Sparkles className="text-indigo-500 shrink-0" size={22} />
            <div>
              <h4 className="text-sm font-bold text-theme-main">Integración Rápida con BotADesk</h4>
              <p className="text-xs text-theme-muted font-medium mt-0.5">
                Configura de forma instantánea las reglas de despacho de alertas que provienen de BotADesk.
              </p>
            </div>
          </div>

          <div className="text-xs space-y-2.5 text-theme-muted leading-relaxed">
            <p>
              Para que WhatyBot despache automáticamente las alertas enviadas por <strong>BotADesk</strong> (con el tipo de evento <code>notificacion.directa</code>), se requiere mapear la plantilla y regla adecuadas en el sistema.
            </p>
            <div className="p-3.5 bg-theme-base/60 rounded-lg border border-theme space-y-1">
              <p className="font-semibold text-theme-main">¿Qué realiza la configuración automática?</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Crea una regla de notificación para <code>notificacion.directa</code> vinculada al destinatario dinámico (<code>payload_phone</code>).</li>
                <li>Registra la plantilla con el cuerpo de mensaje <code>{"{{message_content}}"}</code> en WhatyBot Go (WhatsMeow) y la API oficial de Meta.</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 pt-1">
            <button
              type="button"
              disabled={configuringBotADesk}
              onClick={handleAutoConfigureBotADesk}
              className="bg-indigo-600 hover:bg-indigo-500 text-white px-5 py-2.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 shadow-md hover:shadow-lg disabled:opacity-50"
            >
              <Sparkles size={14} className="animate-pulse" />
              {configuringBotADesk ? 'Configurando...' : '⚡ Configurar Automáticamente BotADesk'}
            </button>
          </div>
        </div>
      </div>

      {/* Historial de Eventos / Webhooks de API */}
      <div className="mt-6 border-t border-theme pt-6 space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-bold text-theme-main flex items-center gap-2">
            <Database size={18} className="text-indigo-500" />
            Historial Reciente de Webhooks / Eventos de API
          </h4>
          <button
            type="button"
            onClick={() => fetchEventLogs(orgId || undefined)}
            disabled={loadingEventLogs || !orgId}
            className="text-xs text-primary-600 hover:underline flex items-center gap-1 disabled:opacity-50"
          >
            <RefreshCw size={12} className={loadingEventLogs ? 'animate-spin' : ''} />
            Actualizar
          </button>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-theme-muted" size={16} />
          <input
            type="text"
            placeholder="Buscar eventos por ID, tipo de evento, estado, payload, error..."
            className="w-full pl-10 pr-4 py-2 border border-theme rounded-lg text-xs bg-theme-card text-theme-main"
            value={webhookSearch}
            onChange={(e) => setWebhookSearch(e.target.value)}
          />
        </div>

        {loadingEventLogs ? (
          <p className="text-xs text-theme-muted text-center py-4">Cargando historial de eventos...</p>
        ) : eventLogs.length === 0 ? (
          <p className="text-xs text-theme-muted text-center py-4 bg-theme-base/30 rounded-lg border border-theme border-dashed">
            No se han registrado eventos de API o webhooks recientemente en esta organización.
          </p>
        ) : (() => {
          const filteredLogs = eventLogs.filter((log) => {
            const searchLower = webhookSearch.toLowerCase();
            const idStr = `#${log.id}`;
            const eventType = (log.event_type || '').toLowerCase();
            const status = (log.status || '').toLowerCase();
            const errMsg = (log.error_message || '').toLowerCase();
            const payloadStr = JSON.stringify(log.payload || {}).toLowerCase();
            return idStr.includes(searchLower) ||
                   eventType.includes(searchLower) ||
                   status.includes(searchLower) ||
                   errMsg.includes(searchLower) ||
                   payloadStr.includes(searchLower);
          });

          return (
            <div className="max-h-[350px] overflow-y-auto rounded-xl border border-theme">
              <table className="w-full text-left border-collapse bg-theme-card relative">
                <thead>
                  <tr className="bg-theme-base sticky top-0 backdrop-blur-md z-10 text-[10px] font-bold text-theme-muted uppercase tracking-wider border-b border-theme">
                    <th className="px-4 py-2.5">ID / Fecha</th>
                    <th className="px-4 py-2.5">Evento</th>
                    <th className="px-4 py-2.5">Estado</th>
                    <th className="px-4 py-2.5">Detalles / Error</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-theme text-xs">
                  {filteredLogs.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center py-6 text-theme-muted">
                        No se encontraron eventos coincidentes con la búsqueda.
                      </td>
                    </tr>
                  ) : (
                    filteredLogs.map((log) => {
                      const dateStr = log.created_at ? new Date(log.created_at).toLocaleString() : 'N/A';
                      const statusColors: any = {
                        pending: 'bg-amber-50 text-amber-600 dark:bg-amber-950/20 dark:text-amber-400 border-amber-100 dark:border-amber-900/50',
                        processed: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/20 dark:text-emerald-400 border-emerald-100 dark:border-emerald-900/50',
                        failed: 'bg-red-50 text-red-650 dark:bg-red-950/20 dark:text-red-400 border-red-100 dark:border-red-900/50',
                        ignored: 'bg-slate-50 text-slate-600 dark:bg-slate-900/40 dark:text-slate-400 border-slate-100 dark:border-slate-800'
                      };
                      return (
                        <tr key={log.id} className="hover:bg-theme-base/30 transition-colors">
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="font-semibold text-theme-main">#{log.id}</div>
                            <div className="text-[10px] text-theme-muted mt-0.5">{dateStr}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-mono text-[11px] bg-theme-base px-1.5 py-0.5 rounded text-theme-main border border-theme">
                              {log.event_type}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-block text-[9px] px-2 py-0.5 rounded font-semibold border ${statusColors[log.status] || 'bg-slate-50 text-slate-600'}`}>
                              {log.status.toUpperCase()}
                            </span>
                          </td>
                          <td className="px-4 py-3 max-w-xs truncate">
                            {log.error_message ? (
                              <span className="text-red-500 font-medium break-words text-[11px] block max-h-12 overflow-y-auto" title={log.error_message}>
                                ⚠️ {log.error_message}
                              </span>
                            ) : (
                              <span className="text-theme-muted text-[10px] block truncate" title={JSON.stringify(log.payload)}>
                                {JSON.stringify(log.payload)}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>

      <ConfirmModal
        isOpen={showDeleteKeyConfirm}
        title="Eliminar API Key"
        message="¿Estás seguro de que deseas eliminar y revocar esta API Key? Cualquier integración externa que use esta clave dejará de funcionar inmediatamente."
        onConfirm={confirmDeleteApiKey}
        onCancel={() => {
          setShowDeleteKeyConfirm(false);
          setKeyToDeleteId(null);
        }}
        confirmText="Eliminar Key"
        confirmVariant="danger"
      />
    </div>
  );
};
